import { createBlock, generateId } from './create';

export const isLayoutContainer = (block) =>
  block.type === 'row' || block.type === 'column' || block.type === 'choice-path';

export const wrapInColumn = (block) => ({
  id: generateId(),
  type: 'column',
  content: '',
  props: {},
  children: [block],
});

export const wrapInRow = (leftBlock, rightBlock) => ({
  id: generateId(),
  type: 'row',
  content: '',
  props: {},
  children: [wrapInColumn(leftBlock), wrapInColumn(rightBlock)],
});

// Remove empty columns/rows and unwrap rows left with a single column
export const cleanupEmptyContainers = (blocks) =>
  blocks.reduce((acc, block) => {
    let current = block;
    if (current.children?.length) {
      current = { ...current, children: cleanupEmptyContainers(current.children) };
    }
    if (current.type === 'row') {
      const columns = (current.children || []).filter(
        (col) => col.type !== 'column' || (col.children && col.children.length > 0)
      );
      if (columns.length === 0) return acc;
      if (columns.length === 1) {
        // One column left → its content flows back to full width, in place
        const remaining = columns[0].type === 'column' ? columns[0].children : [columns[0]];
        return [...acc, ...remaining];
      }
      if (columns !== current.children) current = { ...current, children: columns };
    }
    return [...acc, current];
  }, []);

// A callout must always hold at least one block so there's somewhere to type
export const ensureCalloutsHaveChildren = (blocks) =>
  blocks.map((block) => {
    if (block.type === 'callout' && (!block.children || block.children.length === 0)) {
      return { ...block, children: [createBlock('text')] };
    }
    if (block.children?.length) {
      return { ...block, children: ensureCalloutsHaveChildren(block.children) };
    }
    return block;
  });

/** Keep every choice on at least one path, and every path on at least one block. */
export const ensureChoicesHavePaths = (blocks) => {
  let changed = false;
  const next = (blocks || []).map((block) => {
    let current = block;
    if (current.type === 'choice') {
      let paths = (current.children || []).filter((child) => child.type === 'choice-path');
      if (paths.length === 0) {
        const first = createBlock('choice-path', 'Option A');
        first.children = [createBlock('text')];
        const second = createBlock('choice-path', 'Option B');
        second.children = [createBlock('text')];
        paths = [first, second];
        current = {
          ...current,
          props: { ...current.props, activePathId: first.id, showAll: false },
          children: paths,
        };
        changed = true;
      }
      const filled = paths.map((path) => {
        if (path.children?.length) {
          const children = ensureChoicesHavePaths(path.children);
          if (children !== path.children) {
            changed = true;
            return { ...path, children };
          }
          return path;
        }
        changed = true;
        return { ...path, children: [createBlock('text')] };
      });
      const extras = (current.children || []).filter((child) => child.type !== 'choice-path');
      const children = extras.length ? [...filled, ...extras] : filled;
      const wanted = current.props?.activePathId;
      const activePathId =
        wanted && filled.some((path) => path.id === wanted) ? wanted : filled[0].id;
      const props =
        current.props?.activePathId === activePathId
          ? current.props
          : { ...current.props, activePathId };
      if (children !== current.children || props !== current.props) {
        changed = true;
        current = { ...current, children, props };
      }
      return current;
    }
    if (current.type === 'choice-path' && (!current.children || current.children.length === 0)) {
      changed = true;
      current = { ...current, children: [createBlock('text')] };
    }
    if (current.children?.length) {
      const children = ensureChoicesHavePaths(current.children);
      if (children !== current.children) {
        changed = true;
        current = { ...current, children };
      }
    }
    return current;
  });
  return changed ? next : blocks;
};

export const ensureContainerInvariants = (blocks) =>
  ensureChoicesHavePaths(ensureCalloutsHaveChildren(blocks));
